import { z } from "zod";
import { extendZodWithOpenApi } from "zod-openapi";

// Needed so the transform-bearing schemas below can carry an `effectType`.
// Without it zod-openapi cannot type a ZodEffects in an output position and
// throws while generating /api/openapi, taking the whole document down.
extendZodWithOpenApi(z);

// Query type enum matching the generation system
export const DISCOVERY_QUERY_TYPES = [
	"category_best",
	"use_case_solution",
	"budget_constrained",
	"solution_comparison",
	"segment_focused",
	"local_services",
	"local_comparison",
	"local_reviews",
	"category_entry_point",
	"jobs_to_be_done",
	"buyers_journey_awareness",
	"buyers_journey_consideration",
	"buyers_journey_decision",
	"stakeholder_perspective",
	"brand_identity",
	"brand_comparison",
] as const;

// The original 8 types used for AI Visibility Report generation (do not modify)
export const ORIGINAL_QUERY_TYPES = [
	"category_best",
	"use_case_solution",
	"budget_constrained",
	"solution_comparison",
	"segment_focused",
	"local_services",
	"local_comparison",
	"local_reviews",
] as const;

// Prompt generator framework categories the user can select
export const PROMPT_GENERATOR_FRAMEWORKS = [
	"original",
	"category_entry_points",
	"jobs_to_be_done",
	"buyers_journey",
	"stakeholder_perspectives",
	"brand_prompts",
] as const;

export type PromptGeneratorFramework =
	(typeof PROMPT_GENERATOR_FRAMEWORKS)[number];

export const PROMPT_GENERATOR_FRAMEWORK_LABELS: Record<
	PromptGeneratorFramework,
	string
> = {
	original: "Standard AI Visibility",
	category_entry_points: "Category Entry Points",
	jobs_to_be_done: "Jobs to Be Done",
	buyers_journey: "Buyer's Journey",
	stakeholder_perspectives: "Stakeholder Perspectives",
	brand_prompts: "Brand Prompts",
};

export const PROMPT_GENERATOR_FRAMEWORK_DESCRIPTIONS: Record<
	PromptGeneratorFramework,
	string
> = {
	original:
		"Classic buyer-intent queries: category best, use case, budget, comparison, and segment-focused prompts.",
	category_entry_points:
		"Prompts based on the triggers and cues that cause buyers to enter your category — the moments before they think about any brand.",
	jobs_to_be_done:
		"Prompts framed around the functional, emotional, and social jobs your buyers are trying to accomplish.",
	buyers_journey:
		"Prompts spanning Awareness, Consideration, and Decision stages — from problem discovery to final vendor selection.",
	stakeholder_perspectives:
		"Prompts from the perspective of different stakeholders involved in the purchase decision (e.g., CIO, CFO, end user).",
	brand_prompts:
		"Prompts that ask about your brand by name — identity, trust, offerings, pricing. Measured on expected messages and drift instead of share of voice.",
};

export type DiscoveryQueryType = (typeof DISCOVERY_QUERY_TYPES)[number];

/**
 * Prompt class taxonomy. Brand-focused ("What is [brand]?") and brand
 * comparison ("[brand] vs [competitor]") prompts always mention the brand,
 * so counting them into mention rate / share of voice inflates those
 * metrics. They get their own metric families instead.
 *
 * Note: `solution_comparison` stays a discovery type — it is category-based
 * and names no brands; `brand_comparison` explicitly names our brand
 * against specific competitors.
 */
export type PromptClass = "discovery" | "brand" | "comparison";

export function promptClassFor(queryType: string): PromptClass {
	if (queryType === "brand_identity") {
		return "brand";
	}
	if (queryType === "brand_comparison") {
		return "comparison";
	}
	return "discovery";
}

/** Brand/comparison prompts are excluded from SOV, mention-rate, citation-rate, and global key-message matching. */
export function isExcludedFromDiscoveryMetrics(queryType: string): boolean {
	return promptClassFor(queryType) !== "discovery";
}

/**
 * The discovery-class types only (no brand_identity / brand_comparison).
 * Used by the prompt-generation schemas for non-brand frameworks — their
 * instructions forbid naming the brand, so the LLM must not be able to
 * emit a brand-class type — and by the UI's type grouping.
 */
export const DISCOVERY_CLASS_QUERY_TYPES = DISCOVERY_QUERY_TYPES.filter(
	(type) => promptClassFor(type) === "discovery",
) as unknown as readonly [DiscoveryQueryType, ...DiscoveryQueryType[]];

/** Compared brand name — trimmed, deduped case-insensitively at the array level. */
export const comparisonBrandsSchema = z
	.array(
		z
			.string()
			.min(1)
			.max(100)
			.transform((s) => s.trim())
			.openapi({ effectType: "input" }),
	)
	.max(10, "Up to 10 compared brands per query")
	.transform((brands) => {
		const seen = new Set<string>();
		return brands.filter((b) => {
			const key = b.toLowerCase();
			if (!b || seen.has(key)) {
				return false;
			}
			seen.add(key);
			return true;
		});
	})
	.openapi({ effectType: "input" });

/**
 * Free-form tag string. Users author these themselves on each discovery
 * query so historical metrics can be sliced by any factor that matters to
 * the buyer (location, persona, campaign, etc.). Lowercased + trimmed on
 * write to keep the set canonical. 30-char cap mirrors typical chip UIs.
 */
export const discoveryQueryTagSchema = z
	.string()
	.min(1)
	.max(30)
	.transform((s) => s.trim().toLowerCase())
	.openapi({ effectType: "input" })
	.refine((s) => s.length > 0, "Tag cannot be empty");

/** Tag array helper — deduped + capped at 10 tags per query. */
export const discoveryQueryTagArraySchema = z
	.array(discoveryQueryTagSchema)
	.max(10, "Up to 10 tags per query")
	.transform((tags) => Array.from(new Set(tags)))
	.openapi({ effectType: "input" });

// Base discovery query schema
export const discoveryQuerySchema = z.object({
	id: z.string().cuid(),
	propertyId: z.string(),
	query: z.string().min(3, "Query must be at least 3 characters"),
	queryType: z.enum(DISCOVERY_QUERY_TYPES),
	category: z.string().nullable(),
	useCase: z.string().nullable(),
	budget: z.string().nullable(),
	segment: z.string().nullable(),
	comparisonFocus: z.string().nullable(),
	location: z.string().nullable(),
	isLocal: z.boolean().nullable(),
	tags: z.array(z.string()),
	comparisonBrands: z.array(z.string()),
	isActive: z.boolean(),
	useForAiReport: z.boolean(),
	generatedFromSnapshotId: z.string().nullable(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date(),
});

// Create input schema (excludes auto-generated fields)
export const createDiscoveryQuerySchema = z.object({
	propertyId: z.string(),
	query: z.string().min(3, "Query must be at least 3 characters"),
	queryType: z.enum(DISCOVERY_QUERY_TYPES),
	category: z.string().optional(),
	useCase: z.string().optional(),
	budget: z.string().optional(),
	segment: z.string().optional(),
	comparisonFocus: z.string().optional(),
	location: z.string().optional(),
	isLocal: z.boolean().optional(),
	tags: discoveryQueryTagArraySchema.optional(),
	comparisonBrands: comparisonBrandsSchema.optional(),
	active: z.boolean().optional(),
});

// Update input schema (includes id, other fields optional)
export const updateDiscoveryQuerySchema = z.object({
	id: z.string().cuid(),
	query: z.string().min(3, "Query must be at least 3 characters").optional(),
	queryType: z.enum(DISCOVERY_QUERY_TYPES).optional(),
	category: z.string().nullable().optional(),
	useCase: z.string().nullable().optional(),
	budget: z.string().nullable().optional(),
	segment: z.string().nullable().optional(),
	comparisonFocus: z.string().nullable().optional(),
	location: z.string().nullable().optional(),
	isLocal: z.boolean().nullable().optional(),
	tags: discoveryQueryTagArraySchema.optional(),
	comparisonBrands: comparisonBrandsSchema.optional(),
	active: z.boolean().optional(),
	useForAiReport: z.boolean().optional(),
});

// Bulk regenerate input
export const bulkRegenerateDiscoveryQueriesSchema = z.object({
	propertyId: z.string(),
	location: z
		.object({
			city: z.string().optional(),
			region: z.string().optional(),
			country: z.string().optional(),
		})
		.optional(),
	forceRegenerate: z.boolean().default(false),
});

// Type exports
export type DiscoveryQuery = z.infer<typeof discoveryQuerySchema>;
export type CreateDiscoveryQueryInput = z.infer<
	typeof createDiscoveryQuerySchema
>;
export type UpdateDiscoveryQueryInput = z.infer<
	typeof updateDiscoveryQuerySchema
>;
export type BulkRegenerateDiscoveryQueriesInput = z.infer<
	typeof bulkRegenerateDiscoveryQueriesSchema
>;

// Validation error type
export interface DiscoveryQueryValidationError {
	field: string;
	message: string;
	code: string;
}

// Query type display names
export const DISCOVERY_QUERY_TYPE_LABELS: Record<DiscoveryQueryType, string> = {
	category_best: "Category Best",
	use_case_solution: "Use Case Solution",
	budget_constrained: "Budget Constrained",
	solution_comparison: "Solution Comparison",
	segment_focused: "Segment Focused",
	local_services: "Local Services",
	local_comparison: "Local Comparison",
	local_reviews: "Local Reviews",
	category_entry_point: "Category Entry Point",
	jobs_to_be_done: "Jobs to Be Done",
	buyers_journey_awareness: "Buyer's Journey (Awareness)",
	buyers_journey_consideration: "Buyer's Journey (Consideration)",
	buyers_journey_decision: "Buyer's Journey (Decision)",
	stakeholder_perspective: "Stakeholder Perspective",
	brand_identity: "Brand Identity",
	brand_comparison: "Brand Comparison",
};

// Query type descriptions
export const DISCOVERY_QUERY_TYPE_DESCRIPTIONS: Record<
	DiscoveryQueryType,
	string
> = {
	category_best:
		"What are the best [category] solutions for [segment/use case]?",
	use_case_solution: "Who can help with [specific problem/workflow]?",
	budget_constrained:
		"Affordable [category] solutions/services under [budget range]",
	solution_comparison:
		"Compare different [category] solutions/providers for [use case]",
	segment_focused: "Best [category] for [specific business type/size]",
	local_services: "Best [category] services near [location]",
	local_comparison: "Compare [category] providers in [location]",
	local_reviews: "Top rated [category] in [location]",
	category_entry_point:
		"When a buyer encounters [trigger/need], what [category] comes to mind?",
	jobs_to_be_done: "I need to [job outcome] — what solutions can help?",
	buyers_journey_awareness:
		"What is [problem/challenge] and how do companies solve it?",
	buyers_journey_consideration:
		"What are the different approaches to [solving problem]?",
	buyers_journey_decision:
		"Which [category] solution is best for [specific requirements]?",
	stakeholder_perspective:
		"[Stakeholder role]-focused query about [category] evaluation",
	brand_identity:
		"What is [your brand]? Measured on expected messages and answer drift",
	brand_comparison:
		"[Your brand] vs [named competitors] head-to-head. Measured on who AI favors and why",
};
